import React from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { Card } from '@teetsh/app/src/containers/UserSettings/Trash/components/Card';
import { Table } from '@teetsh/app/src/containers/UserSettings/Trash/components/Table';
import { CellContext } from '@tanstack/react-table';
import { RestorableEmploiDuTemps } from '@teetsh/app/src/types/edt';
import { RestorableProgrammation } from '@teetsh/app/src/types/programmations';
import {
  RestorableSequence,
  RestorableFicheDePrep,
} from '@teetsh/app/src/types/sequences';
import { RestorableEleve } from '@teetsh/app/src/types/eleves';
import { RestorableSchoolyear } from '@teetsh/app/src/types/schoolyear';
import { RestorableSchool } from '@teetsh/app/src/types/school';
import { RestorableClassroom } from '@teetsh/app/src/types/classroom';

export type RestorableItemType =
  | RestorableEmploiDuTemps
  | RestorableProgrammation
  | RestorableSequence
  | RestorableFicheDePrep
  | RestorableEleve
  | RestorableSchoolyear
  | RestorableSchool
  | RestorableClassroom;

export type RestoreFn = (params: {
  id: string;
  schoolyearId?: string;
  schoolId?: string;
  sequenceId?: string;
}) => void;

interface Props<ItemsType> {
  items: ItemsType[];
  restore: RestoreFn;
  cardFileDetails?: (item: ItemsType) => React.ReactElement;
  tableFileDetails?: (p: CellContext<ItemsType, unknown>) => React.ReactElement;
}

export function RestorableList<ItemsType extends RestorableItemType>(
  props: Props<ItemsType>,
) {
  const { items, restore } = props;
  const { t } = useTranslation(['userSettings']);
  const isMobile = useIsBelowBreakpoint('lg');

  if (!items.length) {
    return <div tw="mt-6">{t('userSettings:restore.empty')}</div>;
  }

  if (isMobile) {
    return (
      <div tw="grid space-y-4 divide-y divide-gray-200">
        {items.map(item => (
          <Card<ItemsType>
            key={item.id}
            item={item}
            restore={restore}
            details={props.cardFileDetails}
          />
        ))}
      </div>
    );
  }

  return (
    <Table<ItemsType>
      items={items}
      restore={restore}
      details={props.tableFileDetails}
    />
  );
}
